/* eslint-disable complexity */
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { ActiveMode, BALANCE_SHEET_CATEGORIES, COADisplayAccount, COA_TYPE, P_AND_L_CATEGORIES } from './types';
import { BasicInfo } from './components/basic-info/basic-info';
import { CategorySection } from './components/category-section/category-section';
import { SearchWithActions } from './components/search-with-actions/search-with-actions';
import { Tabs, TABS_HEIGHT } from './components/tabs/tabs';
import { useCoaActions } from './components/context/coa-actions-context';
import { useCoaApiView } from './hooks/useCoaApiView';
import { useCOAGrid } from './hooks/useCoaGrid';
import { useIsEditableCOA } from './hooks';
import { useNavHeight } from '../hooks/useNavHeight';

import * as styles from './chart-of-accounts-container.scss';

interface Props {
  coaType: COA_TYPE;
  displayAccountCodes?: boolean;
  treeData: COADisplayAccount | null;
}

export const ChartOfAccountsContainerUk = ({ coaType, displayAccountCodes, treeData }: Props) => {
  const isCOAEditable = useIsEditableCOA();
  const { navHeight } = useNavHeight();

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  const { handleCreateGroupClick, handleReclassifyClick } = useCoaActions();

  const { saveReorderedTree, isUpdatingStructure } = useCoaApiView(workspaceId, organisationId);

  const handleReorder = useCallback(
    async (draftAccountsTree: COADisplayAccount, coaType: COA_TYPE, onSuccess?: () => void) => {
      saveReorderedTree(draftAccountsTree, coaType, {
        onSuccess: () => {
          onSuccess?.();
        }
      });
    },
    [saveReorderedTree]
  );

  const {
    currentTree,
    isReordering,
    searchText,
    nodeIdsWithParentIds,
    accountNameMap,
    accountsList,
    handleStartReordering,
    handleCancelReordering,
    handleSaveReordering,
    handleSearchChange,
    handleDragAndDropUpdate
  } = useCOAGrid({
    treeData,
    displayAccountCodes,
    onSaveReorder: handleReorder,
    isBalanceSheet: coaType === COA_TYPE.BALANCE_SHEET
  });
  const handleActionButtonClick = useCallback(
    (mode: ActiveMode) => {
      if (mode === ActiveMode.CREATE_GROUP) {
        handleCreateGroupClick();
      } else if (mode === ActiveMode.RECLASSIFYING) {
        handleReclassifyClick();
      } else if (mode === ActiveMode.REORDERING) {
        handleStartReordering();
      }
    },
    [handleCreateGroupClick, handleStartReordering, handleReclassifyClick]
  );

  const categoriesForDisplay = coaType === COA_TYPE.BALANCE_SHEET ? BALANCE_SHEET_CATEGORIES : P_AND_L_CATEGORIES;

  const activeModeForChildren = isReordering ? ActiveMode.REORDERING : undefined;

  return (
    <div className={styles.content}>
      <BasicInfo />
      <Tabs />
      <SearchWithActions
        isCOAEditable={isCOAEditable}
        activeMode={activeModeForChildren}
        handleClose={handleCancelReordering}
        handleSave={handleSaveReordering}
        handleActionButtonClick={handleActionButtonClick}
        searchText={searchText}
        handleSearchChange={handleSearchChange}
        navHeight={navHeight}
        TABS_HEIGHT={TABS_HEIGHT}
        isUpdatingStructure={isUpdatingStructure}
      />
      <div data-testid="coa-categories-container" style={{ marginTop: 6 }}>
        {categoriesForDisplay.map(category => (
          <CategorySection
            nodeIdsWithParentIds={nodeIdsWithParentIds}
            key={category}
            data={accountsList.filter(d => d.category === category)}
            categoryName={category}
            isBalanceSheet={coaType === COA_TYPE.BALANCE_SHEET}
            activeMode={activeModeForChildren}
            onDragAndDropUpdate={handleDragAndDropUpdate}
            accountsTree={currentTree}
            accountNameMap={accountNameMap}
            isUpdatingStructure={isUpdatingStructure}
            handleReorder={handleReorder}
          />
        ))}
      </div>
    </div>
  );
};
